import { useEffect, useState } from "react";

import { AdapterProtocol } from "../../../flatbuffers/generated/archive/adapter-protocol";
import { fetchFlatBufferBinary } from "../../../utils/flatbufferApi";
import { unpackModbusTemplate } from "../../../utils/modbusFlatBuffer";
import type { EditorModbusTemplate } from "../../setupWizard/steps/CdoModbusEditor";
import GETJwtToken from "../../../redux/authentication/authentication";

/**
 * Fetches and unpacks the Modbus CDO template for the State panel.
 *
 * Modbus is the one enumerable protocol whose CDO can't be read from the
 * adapter's `cdoTemplateJson` (Go's Type/Value union wrappers break the TS
 * FlatBuffer Object API), so we pull the FlatBuffer binary from
 * `/adapters/{id}/cdo` and unpack it — the same path AdapterEditor and
 * StepChannelModifiers use. Returns `null` for non-Modbus adapters, before the
 * bytes resolve, or on any fetch/parse failure (the State tab simply shows no
 * Modbus dictionary in that case rather than crashing).
 */
export default function useModbusCdoTemplate(
  adapterId: string,
  protocolType: AdapterProtocol | undefined,
): EditorModbusTemplate | null {
  const [template, setTemplate] = useState<EditorModbusTemplate | null>(null);

  useEffect(() => {
    let cancelled = false;

    if (!adapterId || protocolType !== AdapterProtocol.MODBUS) {
      setTemplate(null);
      return undefined;
    }

    (async () => {
      try {
        const token = await GETJwtToken();
        const apiBase = process.env.REACT_APP_LIFT_NET_CLOUD_API_ONLINE || "";
        const bytes = await fetchFlatBufferBinary(`${apiBase}/adapters/${adapterId}/cdo`, token);
        if (cancelled) return;
        setTemplate(bytes && bytes.length > 0 ? unpackModbusTemplate(bytes) : null);
      } catch {
        if (!cancelled) setTemplate(null);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [adapterId, protocolType]);

  return template;
}
